import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import { TypeChecker } from '@attain-apps/type-guards'

import * as state from '../../../../../../../state'
import * as shared from '../shared'

/**
 * Modal component for selecting responsible persons for a space record.
 */
@ngCore.Component({
  imports: [
    ds.button.Component,
    ds.input.checkbox.Component,
    ds.input.select.single.Component,
    ds.layout.widget.Component,
    ds.overlay.centered.Component,
    shared.employeeSelector.Component,
  ],
  selector: 'atn-space-responsible-persons-modal',
  styleUrl: './component.scss',
  templateUrl: './component.html',
})
export class Component {
  /** Responsible persons collection */
  public readonly collectionSg = ngCore.input.required<
    | state.space.full.recordResponsiblePersons.Collection
    | state.space.full.clusterResponsiblePersons.Collection
  >({ alias: 'collection' })

  /** @see {@link state.space.full.employee.Collection} */
  public readonly employeesEc = ngCore.inject(
    state.space.full.employee.Collection,
  )

  /** Employees not already assigned as responsible persons */
  public readonly employeesSg = ngCore.computed(
    (
      allEmployees = this.employeesEc.plugins.currentListPlugin.feature.selectors.entities.asSignal(),
      responsiblePersonsLookup = this.collectionSg().plugins.cachePlugin.feature.selectors.lookup.asSignal(),
    ) =>
      allEmployees.filter(
        employee =>
          !Object.values(responsiblePersonsLookup).some(
            rp => rp?.employee.id === employee.id,
          ),
      ),
  )

  /** Employees currently checked in the modal — derived from creationsLookup entries */
  public readonly selectedEmployeesSg = ngCore.computed(
    (
      selectedEmployees = this.employeesEc.plugins.selectionPlugin.feature.selectors
        .getAllSelected({
          from: 'applied',
          target: this.collectionSg().associations?.selectionTarget,
        })
        .asSignal(),
    ) => selectedEmployees.filter(TypeChecker.isNotNullish.raw),
  )

  /**
   * @see {@link state.space.full.recordResponsiblePersons.CreateEntity},
   */
  private readonly _creationSg = ngCore.computed(
    (
      creationsLookup = this.collectionSg().plugins.editingPlugin.feature.selectors.creationsLookup.asSignal(),
    ) => {
      const [creation] = Object.values(creationsLookup)
      return creation && 'employeeIds' in creation ? creation : undefined
    },
  )

  /**
   * Options for the primary dropdown: the employees this modal is adding.
   */
  public readonly employeeItemsSg = ngCore.computed<
    ds.input.select.models.Item<ents.extra.space.Employee.Id>[]
  >(
    (
      creation = this._creationSg(),
      employeeLookup = this.employeesEc.plugins.cachePlugin.feature.selectors.lookup.asSignal(),
    ) =>
      (creation?.employeeIds ?? [])
        .map(employeeId => employeeLookup[employeeId])
        .filter(TypeChecker.isNotNullish.raw)
        .map(employee => ({ label: employee.name, value: employee.id })),
  )

  /** Employee picked as the primary in this modal, if the user picked one */
  public readonly primaryEmployeeIdSg = ngCore.computed(
    (creation = this._creationSg()) => creation?.primaryEmployeeId,
  )

  /**
   * Drops the pick once that employee leaves the selection, so that a primary
   * is never submitted for someone who is not being added.
   */
  private readonly _clearPrimaryWhenNoLongerOffered = ngCore.effect(() => {
    const pickedEmployeeId = this.primaryEmployeeIdSg()
    const employeeItems = this.employeeItemsSg()

    if (
      TypeChecker.isNotNullish(pickedEmployeeId) &&
      !employeeItems.some(({ value }) => value === pickedEmployeeId)
    ) {
      this.primaryEmployeeChangedHandler(undefined)
    }
  })

  /** Stores the employee the user picked as the primary */
  public primaryEmployeeChangedHandler = (
    primaryEmployeeId: ents.extra.space.Employee.Id | undefined,
  ): void => {
    this.collectionSg()
      .plugins.editingPlugin.feature.actions.edit.from(
        'ResponsiblePerson.Modal.PrimaryEmployee.Changed',
      )
      .dispatch({ edit: { input: { primaryEmployeeId } } })
  }

  /** Handles checkbox toggles — adds/removes a per-employee creation entry */
  public employeeToggledHandler = (
    id: ents.extra.space.Employee.Id,
    selected: boolean,
  ): void => {
    this.employeesEc.plugins.selectionPlugin.applied.actions.putInSelectionLookup
      .from('ResponsiblePerson.Modal.EmployeeCheckbox.Toggled')
      .dispatch({
        key: this.collectionSg().associations?.selectionTarget,
        value: selected ? { add: id } : { remove: id },
      })
  }

  /** Removes an employee from the selection (tag close button) */
  public removeSelectedEmployee = (
    employeeId: ents.extra.space.Employee.Id,
  ): void => {
    this.employeesEc.plugins.selectionPlugin.applied.actions.putInSelectionLookup
      .from('ResponsiblePerson.Modal.SelectedTag.Removed')
      .dispatch({
        key: this.collectionSg().associations?.selectionTarget,
        value: { remove: employeeId },
      })
  }

  /** Submit — EC collects all creations with employeeId and calls batchCreate */
  public saveClickHandler = (): void => {
    this.collectionSg()
      .plugins.editingPlugin.feature.actions.submit.from(
        'ResponsiblePerson.Modal.SaveButton.Clicked',
      )
      .dispatch({ create: true })
  }

  /** Discard all creations — modal gate closes when current entity is unset */
  public cancelClickHandler = (): void => {
    this.collectionSg()
      .plugins.editingPlugin.feature.actions.discard.from(
        'ResponsiblePerson.Modal.CancelButton.Clicked',
      )
      .dispatch({ create: true })
  }

  /**
   * Handles the "Add as occupant" checkbox change — updates the current edit
   * with the new value, which will be used by the effect to conditionally add
   * occupants after responsible persons are created.
   */
  public addAsOccupantSelectionChangeHandler = (
    shouldAddAsOccupant: boolean,
  ): void => {
    this.collectionSg()
      .plugins.editingPlugin.feature.actions.edit.from(
        'ResponsiblePerson.Modal.AddAsOccupantCheckbox.Changed',
      )
      .dispatch({
        edit: { input: { addOccupant: shouldAddAsOccupant } },
      })
  }
}
